// =========================================================================
// RigSentinel connectivity module — Phase 1 offline hardening.
//
// Single source of truth for "is the app online?" across the React tree.
// Honors the dev-only "simulate offline" toggle stored in localStorage so QA
// can verify the offline UX on a real connection.
//
// Emits a custom `rs:connectivity` window event on every state change so
// components can subscribe via useEffect without prop-drilling.
// =========================================================================

const SIMULATE_KEY = "rsc.dev.simulateOffline";
const EVENT_NAME = "rs:connectivity";

let _lastEmitted = null;

function _readSimulatedOffline() {
  try {
    return localStorage.getItem(SIMULATE_KEY) === "1";
  } catch {
    return false;
  }
}

export function isSimulatedOffline() {
  return _readSimulatedOffline();
}

export function setSimulatedOffline(on) {
  try {
    if (on) localStorage.setItem(SIMULATE_KEY, "1");
    else localStorage.removeItem(SIMULATE_KEY);
  } catch { /* storage may be unavailable */ }
  // Notify listeners synchronously
  _emit();
}

// Resolved connectivity state. "online" if the browser reports online AND
// the dev override is off; otherwise "offline".
export function getConnectivityState() {
  const browserOnline =
    typeof navigator !== "undefined" ? navigator.onLine !== false : true;
  const simulated = _readSimulatedOffline();
  const online = browserOnline && !simulated;
  return {
    online,
    simulatedOffline: simulated,
    // `reason` helps the indicator pick the right tooltip
    reason: !browserOnline ? "browser" : simulated ? "simulated" : "online",
  };
}

function _emit() {
  if (typeof window === "undefined") return;
  const state = getConnectivityState();
  // Avoid noisy duplicate emissions
  const key = `${state.online}|${state.reason}`;
  if (key === _lastEmitted) return;
  _lastEmitted = key;
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: state }));
  } catch { /* CustomEvent unavailable on very old browsers */ }
}

// Subscribe to connectivity changes. Returns an unsubscribe function.
export function subscribe(listener) {
  if (typeof window === "undefined") return () => {};
  const handler = (e) => listener(e.detail || getConnectivityState());
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}

// Install the global browser-event bridge exactly once. Called from App.js.
let _installed = false;
export function installConnectivityBridge() {
  if (_installed || typeof window === "undefined") return;
  _installed = true;
  window.addEventListener("online", _emit);
  window.addEventListener("offline", _emit);
  // Fire one synthetic emit so subscribers get an initial state without
  // having to call getConnectivityState() themselves.
  setTimeout(_emit, 0);
}

export const __TEST__ = { SIMULATE_KEY, EVENT_NAME };
